import { StepFormLayout } from 'accounts/components/step-form-layout';
import { HOME_NEW_JERSEY_LICENSES_STRINGS } from 'accounts/constants/strings/home-new-jersey-licenses.strings';
import { useSetupFlowStep } from 'accounts/hooks/homeowners/use-setup-flow-step-context';
import Field from 'components/Field/Field';
import { DateInputFinalFormAdapter } from 'components/DateInput/DateInput';
import { maskMMDDYYYY, unmaskMMDDYYYY } from 'components/Input/masks';
import {
  MEDIA_LABEL,
  NewMediaUploadAdapter,
  validateMedia,
} from 'components/NewMediaUploader/NewMediaUploader';
import {
  SelectorFinalFormAdapter,
  SELECTOR_TYPES,
} from 'components/Selector/Selector';
import { Row, Col } from 'forkedlibraries/react-bootstrap';
import FormLayout from 'layouts/FormLayout/FormLayout';
import { US_STATES } from '@belong/common';
import { IMAGE_FILE_TYPE, StepStatus } from 'models/enums/index';
import { uploadFiles } from 'store/redux/images/actions';
import {
  required,
  composeValidators,
  dateValidationMMDDYY,
} from 'utils/validation';
import { getString } from 'strings/index';
import GreenInfoBox from 'components/GreenInfoBox/GreenInfoBox';
import { useAppDispatch } from 'store/redux/use-app-dispatch';

function parseDateMMDDYYYY(value: string): Date | null {
  if (!value) return null;
  const parts = value.split('/');
  if (parts.length !== 3) return null;
  const [month, day, year] = parts.map(Number);
  if (!month || !day || !year || String(year).length < 4) return null;
  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime())) return null;
  return date;
}

const dateInPast = (value: string) => {
  const date = parseDateMMDDYYYY(value);
  if (!date) return undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date > today) return 'Date must be in the past';
};

const dateWithinThreeYears = (value: string) => {
  const date = parseDateMMDDYYYY(value);
  if (!date) return undefined;
  const threeYearsAgo = new Date();
  threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
  if (date < threeYearsAgo) return 'Certificate must be less than 3 years old';
};

function isDateEntryValid(dateValue: string | undefined): boolean {
  if (!dateValue) return false;
  return !dateInPast(dateValue) && !dateValidationMMDDYY(dateValue);
}

function isDateExpired(dateValue: string | undefined): boolean {
  if (!dateValue) return false;
  if (!isDateEntryValid(dateValue)) return false;
  return !!dateWithinThreeYears(dateValue);
}

const customDateValidation = (values: Record<string, unknown>) => {
  if (values?.leadCertificationType !== 'leadSafe') return {};
  const dateValue = values?.dateIssued as string;
  if (!dateValue) return {};

  const formatError = dateValidationMMDDYY(dateValue);
  if (formatError) {
    return { keys: { dateIssued: 'Please enter a valid date (MM/DD/YYYY)' } };
  }

  const pastError = dateInPast(dateValue);
  if (pastError) {
    return { keys: { dateIssued: pastError } };
  }

  return {};
};

const LEAD_CERTIFICATION_OPTIONS = {
  LEAD_SAFE: 'leadSafe',
  LEAD_FREE: 'leadFree',
  NO_VALID: 'noValid',
};

export function HomeNewJerseyLicenses() {
  const dispatch = useAppDispatch();
  const flowStep = useSetupFlowStep();
  const {
    currentStepFormData,
    currentStep,
    selectedProperty,
    goPreviousStep,
    handleSaveAndNext,
    setLoading,
  } = flowStep || {};

  const stateCode = selectedProperty?.address?.state as
    | keyof typeof US_STATES
    | undefined;
  const stateName =
    (stateCode && US_STATES[stateCode]?.name) || stateCode || '';

  const title = getString(HOME_NEW_JERSEY_LICENSES_STRINGS.title, {
    stateName,
  });

  function getInitialValues() {
    if (!currentStepFormData) return {};

    const values: Record<string, unknown> = {};

    if (currentStepFormData.hasLeadSafeCertificate) {
      values.leadCertificationType = LEAD_CERTIFICATION_OPTIONS.LEAD_SAFE;
    } else if (currentStepFormData.hasLeadFreeCertification) {
      values.leadCertificationType = LEAD_CERTIFICATION_OPTIONS.LEAD_FREE;
    } else if (currentStep?.status !== StepStatus.Created) {
      values.leadCertificationType = LEAD_CERTIFICATION_OPTIONS.NO_VALID;
    }

    if (currentStepFormData.certificateIssuedOn) {
      const date = new Date(currentStepFormData.certificateIssuedOn);
      if (!isNaN(date.getTime())) {
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        const year = date.getUTCFullYear();
        values.dateIssued = `${month}/${day}/${year}`;
      }
    }

    if (currentStepFormData.certificateFile?.uniqueId) {
      values.leadCertificateFile = {
        availableMedia: [currentStepFormData.certificateFile],
        mediaToDisplay: [currentStepFormData.certificateFile],
        stagingMedia: [],
        deletedMedia: [],
      };
    }

    return values;
  }

  async function handleSubmit(values: Record<string, unknown>) {
    const isLeadSafe =
      values.leadCertificationType === LEAD_CERTIFICATION_OPTIONS.LEAD_SAFE;
    const isLeadFree =
      values.leadCertificationType === LEAD_CERTIFICATION_OPTIONS.LEAD_FREE;

    const payload: Record<string, unknown> = {
      hasLeadSafeCertificate: isLeadSafe,
      hasLeadFreeCertification: isLeadFree,
    };

    if (isLeadSafe && values.dateIssued) {
      const date = parseDateMMDDYYYY(values.dateIssued as string);
      if (date) {
        payload.certificateIssuedOn = date.toISOString();
      }
    }

    if (isLeadSafe || isLeadFree) {
      setLoading?.(true);
      try {
        const uploadedFiles = await dispatch(
          uploadFiles(
            IMAGE_FILE_TYPE.LeadSafeCertificate,
            values.leadCertificateFile as Record<string, unknown>,
          ),
        );
        if (uploadedFiles?.length) {
          payload.certificateFile = uploadedFiles[0];
        }
      } catch (err) {
        console.error(err);
        setLoading?.(false);
        return;
      }
      setLoading?.(false);
    }

    await handleSaveAndNext?.(payload);
  }

  return (
    <StepFormLayout
      onSubmit={handleSubmit}
      handleBackStep={goPreviousStep}
      title={title}
      subTitle={HOME_NEW_JERSEY_LICENSES_STRINGS.subtitle}
      initialValues={getInitialValues()}
      customValidation={customDateValidation}
      customError="dateIssued"
      getForm={({ values }: any) => (
        <div className="px-sm">
          <FormLayout.Section firstSection>
            <Row>
              <Col md={24}>
                <Field
                  component={SelectorFinalFormAdapter}
                  name="leadCertificationType"
                  validate={required}
                  fluid
                  type={SELECTOR_TYPES.MEDIUMTEXTBUTTON}
                  buttons={[
                    {
                      label: HOME_NEW_JERSEY_LICENSES_STRINGS.lead_safe_label,
                      subLabel:
                        HOME_NEW_JERSEY_LICENSES_STRINGS.lead_safe_sublabel,
                      key: LEAD_CERTIFICATION_OPTIONS.LEAD_SAFE,
                      centerText: true,
                    },
                    {
                      label: HOME_NEW_JERSEY_LICENSES_STRINGS.lead_free_label,
                      key: LEAD_CERTIFICATION_OPTIONS.LEAD_FREE,
                    },
                    {
                      label: HOME_NEW_JERSEY_LICENSES_STRINGS.no_valid_label,
                      key: LEAD_CERTIFICATION_OPTIONS.NO_VALID,
                    },
                  ]}
                />

                {/* Option 1: Lead-Safe — file upload + date */}
                {values?.leadCertificationType ===
                  LEAD_CERTIFICATION_OPTIONS.LEAD_SAFE && (
                  <>
                    <div className="mt-lg">
                      <Field
                        name="leadCertificateFile"
                        validate={validateMedia}
                        component={NewMediaUploadAdapter}
                        mediaLabel={MEDIA_LABEL.FILE_UPLOAD}
                        customLabel={
                          HOME_NEW_JERSEY_LICENSES_STRINGS.lead_safe_file_upload_label
                        }
                        disableWebcam
                        cleanOnMediaChange
                        isSingleMedia
                        ignoreError={false}
                        size="fluid"
                      />
                    </div>

                    <div className="mt-lg">
                      <Field
                        validate={composeValidators(
                          required,
                          dateValidationMMDDYY,
                          dateInPast,
                        )}
                        mask={maskMMDDYYYY}
                        unmask={unmaskMMDDYYYY}
                        placeholder={
                          HOME_NEW_JERSEY_LICENSES_STRINGS.date_issued_placeholder
                        }
                        component={DateInputFinalFormAdapter}
                        name="dateIssued"
                      />
                    </div>

                    {isDateExpired(values?.dateIssued) && (
                      <div className="mt-lg rounded border border-solid border-red bg-red-translucent-light p-lg">
                        {HOME_NEW_JERSEY_LICENSES_STRINGS.no_valid_warning}
                      </div>
                    )}

                    {isDateEntryValid(values?.dateIssued) &&
                      !isDateExpired(values?.dateIssued) && (
                        <div className="mt-lg">
                          <GreenInfoBox>
                            <GreenInfoBox.RegularText>
                              {
                                HOME_NEW_JERSEY_LICENSES_STRINGS.lead_safe_valid_message
                              }
                            </GreenInfoBox.RegularText>
                          </GreenInfoBox>
                        </div>
                      )}
                  </>
                )}

                {/* Option 2: Lead-Free — description + file upload, no date */}
                {values?.leadCertificationType ===
                  LEAD_CERTIFICATION_OPTIONS.LEAD_FREE && (
                  <>
                    <p className="mt-2xl text-h3-xl font-semibold text-navy">
                      {HOME_NEW_JERSEY_LICENSES_STRINGS.lead_free_heading}
                    </p>
                    <p className="mt-xs text-body text-gray">
                      {HOME_NEW_JERSEY_LICENSES_STRINGS.lead_free_description}
                    </p>

                    <div className="mt-0">
                      <Field
                        name="leadCertificateFile"
                        validate={validateMedia}
                        component={NewMediaUploadAdapter}
                        mediaLabel={MEDIA_LABEL.FILE_UPLOAD}
                        customLabel={
                          HOME_NEW_JERSEY_LICENSES_STRINGS.lead_free_file_upload_label
                        }
                        disableWebcam
                        cleanOnMediaChange
                        isSingleMedia
                        ignoreError={false}
                        size="fluid"
                      />
                    </div>
                  </>
                )}

                {/* Option 3: No valid — red warning */}
                {values?.leadCertificationType ===
                  LEAD_CERTIFICATION_OPTIONS.NO_VALID && (
                  <div className="mt-lg rounded border border-solid border-red bg-red-translucent-light p-lg">
                    {HOME_NEW_JERSEY_LICENSES_STRINGS.no_valid_warning}
                  </div>
                )}
              </Col>
            </Row>
          </FormLayout.Section>
        </div>
      )}
    />
  );
}
